import Raven from '@snapchat/sc-raven-js';
import {
  BUILD_VERSION,
  DEV,
  ENVIRONMENT,
  PRODUCTION,
  PUBLIC_SENTRY_DATA_SOURCE_NAME,
  SSO_CONTINUE_ENDPOINT,
  TEST,
} from '../constants';
import { isNotEmpty } from './array';
import { isEmptyString } from './object';
import { executeOnceForKey } from './behavior';
import Log from '../model/Log';
import { warn } from './log';
import getLocale from '../lang/util/getLocale';

// @todo(chuti) move this file to /debug/sentry
const TEST_ENVIRONMENT = DEV || TEST;
// Do not log Raven Events in DEV or TEST environments.
const SENTRY_DISABLED = TEST_ENVIRONMENT;

/**
 * @function captureException Manually capture an exception statement and send it over to Sentry.
 * @param  {error|string} exception the error to log
 * @param  {object} extra information to log with the event
 * @param  {object} tags Key/value pairs which generate breakdowns charts and search filters
 * @param  {array[*]} fingerprint data, that is hashed, to group events into a single issue
 */
export const captureException = (
  exception,
  extra,
  tags = null,
  fingerprint = null,
) => {
  if (SENTRY_DISABLED) return;

  Raven.captureException(exception, { extra, fingerprint, tags });
};

/**
 * @function captureDebug Manually capture a debug statement and send it over to Sentry.
 * @param  {error|string} info the information to log
 * @param  {object} extra information to log with the event
 * @param  {object} tags Key/value pairs which generate breakdowns charts and search filters
 * @param  {array[*]} fingerprint data, that is hashed, to group events into a single issue
 */
export const captureDebug = (info, extra, tags = null, fingerprint = null) => {
  if (SENTRY_DISABLED) return;

  Raven.captureException(info, { extra, fingerprint, tags, level: 'debug' });
};

/**
 * @function captureLog Manually capture a Log object and send it over to Sentry.
 * @param  {Log} log the log metadata to send to Sentry
 */
export const captureLog = (log) => {
  if (SENTRY_DISABLED) return;

  if (!Log.isLog(log) || isEmptyString(log.message)) return;
  Raven.captureException(log.message, log.metadata);
};

/**
 * @function captureDebugOnce Manually capture a debug statement and send it over to Sentry.
 * Limits sending message once per unique {info}
 * @param  {error|string} info the information to log
 * @param  {object} extra information to log with the event
 * @param  {object} tags Key/value pairs which generate breakdowns charts and search filters
 * @param  {array[*]} fingerprint data, that is hashed, to group events into a single issue
 */
export const captureDebugOnce = (
  info,
  extra,
  tags = null,
  fingerprint = null,
) => {
  executeOnceForKey(`SENTRY_DEBUG::${info.toString()}`, () =>
    captureDebug(info, extra, tags, fingerprint),
  );
};

/**
 * @function captureExceptionOnce Manually capture an exception statement and send it over to Sentry.
 * Limits sending message once per unique {exception}
 * @param  {object} extra information to log with the event
 * @param  {object} tags Key/value pairs which generate breakdowns charts and search filters
 * @param  {array[*]} fingerprint data, that is hashed, to group events into a single issue
 */
export const captureExceptionOnce = (
  exception,
  extra,
  tags = null,
  fingerprint = null,
) => {
  executeOnceForKey(`SENTRY_EXCEPTION::${exception.toString()}`, () =>
    captureException(exception, extra, tags, fingerprint),
  );
};

const SSO_REGEX = new RegExp(SSO_CONTINUE_ENDPOINT, 'i');
const breadcrumbCallback = (breadcrumb) => {
  if (breadcrumb.type === 'http' && breadcrumb.category === 'xhr') {
    const { data } = breadcrumb;
    if (data.url && data.url.match(SSO_REGEX)) {
      return false;
    }
  }

  return breadcrumb;
};

export const setUpSentry = (locale) => {
  if (SENTRY_DISABLED) return;

  // Turn allowlist urls in to RegEx
  let allowlistUrls = ['block_all']; // The default;
  const environmentAllowlistUrls = ENVIRONMENT.SENTRY_ALLOWLIST_URLS;
  if (isNotEmpty(environmentAllowlistUrls)) {
    allowlistUrls = environmentAllowlistUrls.map(
      (urlString) => new RegExp(urlString, 'i'),
    );
  }

  // Public Sentry DSN - DO NOT USE THE PRIVATE ONE
  // Set environment to Node Environment
  // Set release to current build version
  // Do not debug on production
  Raven.config(PUBLIC_SENTRY_DATA_SOURCE_NAME, {
    breadcrumbCallback,
    whitelistUrls: allowlistUrls,
    environment: ENVIRONMENT.NODE_ENV,
    release: BUILD_VERSION,
    debug: !PRODUCTION,
    tags: { locale: getLocale(true) },
  }).install();

  // Set up handler on window to calm any unhandled promise rejections
  // TODO(lemieux) investigate where these are coming from.
  self.addEventListener('unhandledrejection', (event) => {
    warn('Unhandled promise rejection', event);
    captureExceptionOnce(event);
    event.preventDefault();
  });
};

export default setUpSentry;
